const VERSION_FILE = '/version.txt';
const CHECK_INTERVAL_MS = 10 * 60 * 1000;
const VERSION_PATTERN = /^\d+\.\d+\.\d+$/;
const CHUNK_RELOAD_KEY = 'chunkReloadAt';
const CHUNK_RELOAD_GUARD_MS = 10 * 1000;

// Returns null on any failure (offline, 404, SPA fallback html in dev) so a bad response never triggers a reload
const fetchLatestVersion = async (): Promise<string | null> => {
  try {
    const response = await fetch(`${VERSION_FILE}?t=${Date.now()}`, { cache: 'no-store' });

    if (!response.ok) {
      return null;
    }

    const version = (await response.text()).trim();

    return VERSION_PATTERN.test(version) ? version : null;
  } catch {
    return null;
  }
};

/**
 * Reloads the page when a new build is deployed, so an open tab never keeps asking for
 * hashed chunk files that no longer exist (which freezes navigation until a manual refresh).
 *
 * The version this tab was loaded with is captured on the first successful check; any later
 * difference means a deploy happened. Checks run on an interval and whenever the tab becomes
 * visible again, because background tabs throttle timers.
 */
export const initVersionChecker = (): void => {
  let loadedVersion: string | null = null;

  const checkVersion = async () => {
    const latestVersion = await fetchLatestVersion();

    if (!latestVersion) {
      return;
    }

    if (!loadedVersion) {
      loadedVersion = latestVersion;

      return;
    }

    if (latestVersion !== loadedVersion) {
      window.location.reload();
    }
  };

  checkVersion();

  setInterval(checkVersion, CHECK_INTERVAL_MS);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkVersion();
    }
  });

  // Safety net for the gap between a deploy and the next version check: a lazy chunk that 404s
  // surfaces as `vite:preloadError`. Reload once to pick up the new build; the timestamp guard
  // stops a reload loop if the chunk is genuinely broken rather than just stale.
  window.addEventListener('vite:preloadError', event => {
    event.preventDefault();

    const lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);

    if (Date.now() - lastReloadAt < CHUNK_RELOAD_GUARD_MS) {
      return;
    }

    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    window.location.reload();
  });
};
